// Kein Stempel darf verlorengehen.
//
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ WARUM ES DIESE DATEI GIBT                                                ║
// ║                                                                          ║
// ║ Fabian stempelte auf dem iPhone ein, schloss die App — und in der        ║
// ║ Datenbank stand nichts. Nicht ein Eintrag, auch kein Protokoll.          ║
// ║                                                                          ║
// ║ Der Grund liegt im Betriebssystem: iOS bricht laufende Netzanfragen ab,  ║
// ║ sobald die App in den Hintergrund geht. Wer tippt und sofort wegwischt,  ║
// ║ dessen Anfrage stirbt auf halbem Weg. Dasselbe im Funkloch — und der Hof ║
// ║ liegt in Versmold, die Tour in Gütersloh, dazwischen ist Landstrasse.    ║
// ║                                                                          ║
// ║ Arbeitszeiten sind Lohn. Sie dürfen nicht davon abhängen, ob das Netz    ║
// ║ gerade steht. Also: erst auf dem Gerät festschreiben, dann übertragen.   ║
// ╚══════════════════════════════════════════════════════════════════════════╝
//
// Der Ablauf:
//   1. Tipp → Eintrag landet SOFORT im localStorage. Das kann nicht scheitern.
//   2. Sofort danach der Versuch, ihn zu senden. Klappt das, fliegt er raus.
//   3. Klappt es nicht, bleibt er liegen — und wird beim nächsten Öffnen der
//      App, beim Zurückkehren in den Vordergrund und sobald das Netz wieder da
//      ist, erneut versucht.
//
// Die Oberfläche liest die Warteschlange mit: Ein noch nicht übertragener
// Stempel zählt als eingestempelt. Sonst tippt jemand ein zweites Mal, weil er
// glaubt, es habe nicht geklappt.

import { supabase } from "@/lib/supabase";

const SCHLUESSEL = "sirges-stempel-warteschlange";

export interface OffenerStempel {
  /** Vom Gerät vergeben — damit ein doppelt gesendeter Stempel nicht doppelt landet. */
  id: string;
  profile_id: string;
  started_at: string;
  /** Wie oft schon vergeblich versucht? Nur zur Anzeige, nicht zum Aufgeben. */
  versuche: number;
}

export function warteschlangeLesen(): OffenerStempel[] {
  try {
    const roh = localStorage.getItem(SCHLUESSEL);
    if (!roh) return [];
    const liste = JSON.parse(roh);
    return Array.isArray(liste) ? liste : [];
  } catch {
    // Kaputter Inhalt darf die Stempeluhr nicht lahmlegen.
    return [];
  }
}

function warteschlangeSchreiben(liste: OffenerStempel[]) {
  try {
    localStorage.setItem(SCHLUESSEL, JSON.stringify(liste));
  } catch {
    /* Speicher voll oder gesperrt — dann bleibt nur der Direktversand. */
  }
}

export function inWarteschlange(eintrag: OffenerStempel) {
  // Nur EIN offener Stempel je Person. Zwei wären ohnehin nicht speicherbar (die
  // Datenbank lässt nur eine offene Schicht zu) — und der zweite würde dann
  // ewig als "nicht übertragen" liegenbleiben und den Mitarbeiter beunruhigen.
  const liste = warteschlangeLesen();
  if (liste.some((e) => e.profile_id === eintrag.profile_id)) return;
  warteschlangeSchreiben([...liste, eintrag]);
}

function ausWarteschlange(id: string) {
  warteschlangeSchreiben(warteschlangeLesen().filter((e) => e.id !== id));
}

/**
 * Versucht, alles Liegengebliebene zu übertragen.
 *
 * Gibt zurück, wie viele noch offen sind — damit die Oberfläche es anzeigen kann.
 * Wirft NICHT: Ein gescheiterter Übertragungsversuch ist kein Fehler, sondern
 * der Normalfall im Funkloch. Der Eintrag bleibt einfach liegen.
 */
/**
 * Läuft gerade ein Sendeversuch?
 *
 * Ohne diese Sperre schicken zwei Auslöser (App-Start und die Abfrage der
 * laufenden Schicht) denselben Stempel gleichzeitig los. Einer gewinnt, der
 * andere prallt an der Regel "nur eine offene Schicht" ab — und in den
 * Protokollen stehen Fehler, die nach einem echten Problem aussehen.
 */
let laeuft: Promise<number> | null = null;

export async function warteschlangeSenden(): Promise<number> {
  if (laeuft) return laeuft;
  laeuft = sendenIntern().finally(() => {
    laeuft = null;
  });
  return laeuft;
}

async function sendenIntern(): Promise<number> {
  const liste = warteschlangeLesen();
  if (!liste.length) return 0;

  for (const e of liste) {
    try {
      // `nachtrag` statt `stempeluhr`: Der Eintrag ist älter als die erlaubten
      // fünf Minuten. Die Datenbank lässt ihn deshalb nur unter diesem Namen
      // durch — und man sieht später, dass er nachgereicht wurde.
      const alter = Date.now() - new Date(e.started_at).getTime();
      const quelle = alter > 4 * 60_000 ? "nachtrag" : "stempeluhr";

      const { error } = await supabase.from("time_entries").insert({
        id: e.id,
        profile_id: e.profile_id,
        started_at: e.started_at,
        quelle,
      });

      // Erledigt ist auch, was schon da ist:
      //   23505 auf dem Schlüssel  → derselbe Stempel kam bereits an
      //   23505 auf "eine offene Schicht" → die Person IST eingestempelt
      // Beides heisst: Die Arbeitszeit ist erfasst. Weiter zu senden würde nur
      // endlos scheitern und den Mitarbeiter mit einem Hinweis behelligen, der
      // nie verschwindet.
      const schonDa =
        error &&
        (error.code === "23505" || /duplicate|conflict|bereits|offene/i.test(error.message ?? ""));

      if (!error || schonDa) {
        ausWarteschlange(e.id);
        continue;
      }

      // Älter als 24 Stunden nimmt die Datenbank nicht mehr an. Den Eintrag
      // trotzdem NICHT wegwerfen — er wird sichtbar liegen bleiben, damit die
      // Leitung ihn von Hand nachtragen kann. Lieber ein sichtbarer Rest als
      // eine verschwundene Arbeitszeit.
      warteschlangeSchreiben(
        warteschlangeLesen().map((x) => (x.id === e.id ? { ...x, versuche: x.versuche + 1 } : x))
      );
    } catch {
      warteschlangeSchreiben(
        warteschlangeLesen().map((x) => (x.id === e.id ? { ...x, versuche: x.versuche + 1 } : x))
      );
    }
  }

  return warteschlangeLesen().length;
}

/**
 * Hängt das Senden an alle Gelegenheiten, bei denen wieder Netz da sein könnte.
 *
 * `visibilitychange` ist der wichtigste Haken: Auf dem iPhone ist das der
 * Moment, in dem der Mitarbeiter die App wieder hervorholt — und genau dort war
 * sein Stempel vorher verloren.
 */
export function warteschlangeUeberwachen() {
  const senden = () => {
    void warteschlangeSenden();
  };

  senden();
  window.addEventListener("online", senden);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") senden();
  });
}
